import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";

import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { createTankGauge, updateTankGauge } from "src/app/store/actions/tank-gauges/tank-gauges.actions";
import { AppState, tankGaugeIsLoading } from "src/app/store/reducers";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { WarehouseDTO } from "src/app/models/Deliveries/deliveriesDTO";
import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SiteDeviceTankLevelGaugesDTO, TankGaugeType, TankLevelGaugesDTO } from "src/app/models/tank-gauges/tank-gaugesDTO";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { TankGaugeHelperDialogComponent } from "./tank-gauge-helper-dialog/tank-gauge-helper-dialog.component";

const TANK_GAUGE_FORM_PARAMS = {
  enabled: "enabled",
  tlgCode: "tlgCode",
  serialNumber: "serialNumber",
  description: "description",
  protocol: "protocol",
  additionalData: "additionalData",
};

@Component({
  selector: "app-tank-gauge-form-dialog",
  templateUrl: "./tank-gauge-form-dialog.component.html",
  styleUrls: ["./tank-gauge-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class TankGaugeFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getTankGaugeForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;
  
  siteCode: string;
  currentTankGauge: TankLevelGaugesDTO;

  tankGaugeTypesList: TankGaugeType[];
  warehouseList: WarehouseDTO[];

  tankGaugeDataForm: FormGroup;

  isCreate: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<TankGaugeFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    public dialog: MatDialog
  ) {
    this.isLoading$ = this.store.pipe(select(tankGaugeIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createTankGaugeForm();
    this.setFormValues(this.currentTankGauge);
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentTankGauge = this.data.currentTankGauge;
    this.tankGaugeTypesList = this.data.tankGaugeTypesList;
    this.warehouseList = this.data.warehouseList;
  }

  createTankGaugeForm() {
    this.tankGaugeDataForm = this.fb.group({
      [TANK_GAUGE_FORM_PARAMS.enabled]: [true],
      [TANK_GAUGE_FORM_PARAMS.tlgCode]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [TANK_GAUGE_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [TANK_GAUGE_FORM_PARAMS.description]: [null, Validators.maxLength(45)],
      [TANK_GAUGE_FORM_PARAMS.additionalData]: [null, Validators.required],
      [TANK_GAUGE_FORM_PARAMS.protocol]: this.fb.group({}),
    });
    this.subscribeFormChanges(this.tankGaugeDataForm);
    this.onAdditionalDataChanges(this.tankGaugeDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getTankGaugeFormPristine();
    });
  }

  setFormValues(tankGauge: TankLevelGaugesDTO, duplication?: boolean) {
    this.tankGaugeDataForm.controls.enabled.setValue(
      tankGauge?.enabled ?? true
    );
    this.tankGaugeDataForm.controls.tlgCode.setValue(
      duplication ? null : tankGauge?.tlgCode ?? null
    );
    this.tankGaugeDataForm.controls.serialNumber.setValue(
      duplication ? null : tankGauge?.serialNumber ?? null
    );
    this.tankGaugeDataForm.controls.description.setValue(
      tankGauge?.description ?? null
    );
    this.tankGaugeDataForm.controls.additionalData.setValue(
      tankGauge?.additionalData ? JSON.stringify(tankGauge.additionalData, null, "\t") : null
    );

    setProtocolFormValues(this.tankGaugeDataForm, tankGauge);
  }

  duplicate() {
    this.setFormValues(this.currentTankGauge, true);

    this.isCreate = true;
    this.currentTankGauge = null;
  }

  onSubmit() {
    if (!this.tankGaugeDataForm.valid) {
      this.tankGaugeDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (this.tankGaugeDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const protocolFormGroup = this.tankGaugeDataForm.get(
      TANK_GAUGE_FORM_PARAMS.protocol
    ) as FormGroup;
    let tankGauge: SiteDeviceTankLevelGaugesDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentTankGauge?.code,
      enabled: this.tankGaugeDataForm.controls.enabled.value ?? false,
      tlgCode: this.tankGaugeDataForm.controls.tlgCode.value ?? null,
      serialNumber: this.tankGaugeDataForm.controls.serialNumber.value ?? null,
      description: this.tankGaugeDataForm.controls.description.value ?? null,
      additionalData: this.tankGaugeDataForm.controls.additionalData.value
      ? JSON.parse(this.tankGaugeDataForm.controls.additionalData.value)
      : null,
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
    };

    if (this.isCreate) {
      this.store.dispatch(createTankGauge({ tankGauge }));
    } else {
      this.store.dispatch(updateTankGauge({ tankGauge }));
    }

    this.tankGaugeDataForm.markAsUntouched();
    this.tankGaugeDataForm.markAsPristine();
    this.getTankGaugeFormPristine();
  }

  openHelperDialog() {
    this.dialog.open(TankGaugeHelperDialogComponent, {
      width: '600px',
    });
  }

  getTankGaugeFormPristine() {
    this.getTankGaugeForm.emit(this.tankGaugeDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.tankGaugeDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }

  private onAdditionalDataChanges(form: FormGroup) {
    form.get(TANK_GAUGE_FORM_PARAMS.additionalData).valueChanges.subscribe((additionalData) => {
      if (additionalData) {
        try {
          const json = JSON.parse(additionalData);
          if (json && typeof json === "object") {
            return json;
          } else {
            form.get(TANK_GAUGE_FORM_PARAMS.additionalData).setErrors({ parseError: true });
          }
        } catch (e) {
          form.get(TANK_GAUGE_FORM_PARAMS.additionalData).setErrors({ parseError: true });
        }
      }
    });
  }
}
